import {
  useNavigate,
  useParams,
  useMatches,
  type AnyRouteMatch,
} from "@tanstack/react-router";
import { useMemo, useCallback } from "react";

import { setLastSelectedSiteId } from "./useLastSelectedSite";
import {
  SITE_ROUTE_CONFIG,
  resolveSiteSwitchRoute,
} from "../config/routeConfig";

interface RouteInfo {
  hasSiteId: boolean;
  isDependentRoute: boolean;
  baseRoute: string | null;
  fallbackRoute: string | null;
  currentMatch?: AnyRouteMatch;
}

export function useSiteNavigation() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const matches = useMatches();

  const currentRouteInfo = useMemo((): RouteInfo => {
    const resolved = resolveSiteSwitchRoute(matches);

    if (!resolved) {
      return {
        hasSiteId: false,
        isDependentRoute: false,
        baseRoute: null,
        fallbackRoute: null,
      };
    }

    return {
      hasSiteId: true,
      isDependentRoute: resolved.config?.hasDependentParams ?? false,
      baseRoute: resolved.targetRouteKey,
      fallbackRoute: resolved.config?.fallbackRoute ?? null,
      currentMatch: resolved.currentMatch,
    };
  }, [matches]);

  const navigateToSite = useCallback(
    (siteId: string) => {
      setLastSelectedSiteId(siteId);

      const targetRouteConfig =
        (currentRouteInfo.baseRoute
          ? SITE_ROUTE_CONFIG[currentRouteInfo.baseRoute]
          : undefined) ?? SITE_ROUTE_CONFIG.dragonTiles;

      void navigate({
        to: targetRouteConfig.path,
        params: { siteId },
        search: () => ({}),
      });
    },
    [currentRouteInfo, navigate],
  );

  return {
    navigateToSite,
    currentRouteInfo,
    siteIdFromParams: params.siteId,
  };
}
